import type { CoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import type { RowInformation } from '../../config/flavourConfig';
import { Table, Box, Button } from '@flos/react-ui';
import Currency from '../Currency/Currency';
import TableRow from '../TableRow/TableRow';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { jobAtom } from '../../recoil/atoms/jobAtom';
import { getImportantInformationCoverableDetailsConfig } from '../../pages/CoverableDetailsPage/utils/CoverableDetailsImportantInformationUtil';
import { paymentFrequencyMap, periodicityUnit } from '../../utils/currencyUtil';
import { currentCoverableIdAtom } from '../../recoil/atoms/currentCoverableIdAtom';
import { excludedFlavorsImportantDetailsConfig } from '../../config/excludedFlavorsConfig';
import { useHippoText } from '../../hooks/useHippoText';
import { businessFlowSelector } from '../../recoil/selectors/getBusinessFlowSelector';
import useWizardNavigation from '../../hooks/useWizardNavigation';
import { useMemo } from 'react';
import { coverableChangesAtom } from '../../recoil/atoms/coverableChangesAtom';
import getCoverableAdjustDetailsConfig from '../../pages/AdjustDetailsPage/config/adjustDetails';
import { IS_OPC_EDIT_AVAILABLE, OPC_EDIT_LOBS } from '../../pages/OpcEntryPage/config/OpcConfig';
import { getProductName } from '../../utils/coverablesUtil';

const ImportantInformation = ({
    coverable,
    shouldRenderPrice = false,
    shouldRenderShoppingBasketDesign = false
}: {
    coverable: CoverableDTO;
    shouldRenderPrice?: boolean;
    shouldRenderShoppingBasketDesign?: boolean;
}): React.JSX.Element => {
    const jobData = useRecoilValue(jobAtom);
    const setCurrentCoverableAtom = useSetRecoilState(currentCoverableIdAtom);
    const coverableChanges = useRecoilValue(coverableChangesAtom);
    const { wizardGotoStep } = useWizardNavigation();
    const getHippoText = useHippoText();
    const businessFlow = useRecoilValue(businessFlowSelector);
    const selectedPaymentFrequency =
        jobData.selectedPaymentFrequency ?? paymentFrequencyMap.monthly;
    const componentProperties = getImportantInformationCoverableDetailsConfig(
        coverable,
        businessFlow,
        getHippoText
    );

    const goToAdjustDetailsPage = (): void => {
        setCurrentCoverableAtom({ fixedId: coverable.fixedId, lobCode: coverable.lobCode });
        wizardGotoStep('AdjustDetailsPage');
    };

    const coverableProperties = componentProperties?.rowsInformation.filter(
        (rowInfo) => !rowInfo.hidden
    );
    const isAdjustable = useMemo((): boolean => {
        const displayedCoverableProperties = coverableProperties?.map((property) => property.key);
        const editableCoverableProperties = getCoverableAdjustDetailsConfig(
            coverable,
            getHippoText,
            businessFlow,
            coverableChanges
        )
            .filter((property) => !property.hidden)
            .map((property) => property.key);
        const isAtLeastOneEditableProperty = displayedCoverableProperties?.some((property) =>
            editableCoverableProperties.includes(property)
        );
        return businessFlow !== 'ONSA' && !!isAtLeastOneEditableProperty;
    }, [businessFlow, coverable, coverableChanges, coverableProperties, getHippoText]);

    const shouldShowAdjustButton = (): boolean => {
        return (
            shouldRenderShoppingBasketDesign &&
            !excludedFlavorsImportantDetailsConfig.adjustDetails.includes(
                coverable.flavourName ?? ''
            ) &&
            isAdjustable &&
            coverable.status === 'Added' &&
            ((IS_OPC_EDIT_AVAILABLE &&
                OPC_EDIT_LOBS.includes(getProductName(coverable.flavourName))) ||
                coverable.lobCode === 'CHO')
        );
    };

    if ((!coverableProperties && !shouldRenderPrice) || businessFlow === 'OPC') {
        return <></>;
    }

    const importantInformationTable = (
        <div className={!shouldRenderShoppingBasketDesign ? 'u-spacing-stack-m' : undefined}>
            {shouldShowAdjustButton() && (
                <div className="u-display-flex@md u-flex-justify-end@md u-display-inline-block">
                    <Button
                        size="small"
                        className="u-spacing-stack-sm"
                        onClick={goToAdjustDetailsPage}
                    >
                        {coverable.flavourName === 'Ejerskifteforsikring'
                            ? getHippoText('DSS-SHOPPING-BASKET-CHOLOB-ADJUSTBTNLABEL')
                            : getHippoText('DSS-SHOPPING-BASKET-ADJUST-COVERABLE')}
                    </Button>
                </div>
            )}
            <Table
                mobileDirection={shouldRenderShoppingBasketDesign ? 'vertical' : undefined}
                theme={shouldRenderShoppingBasketDesign ? 'grey' : undefined}
            >
                <Table.Body>
                    {shouldRenderPrice && (
                        <Table.Row key={coverable?.flavourName}>
                            <Table.Cell className="u-spacing-inset-m--squish-max">
                                <h6 className="u-spacing-stack-bottom-none">Pris</h6>
                            </Table.Cell>
                            <Table.Cell align="right">
                                <Currency
                                    price={
                                        selectedPaymentFrequency === paymentFrequencyMap.monthly
                                            ? coverable.monthlyPrice?.amount ?? 0
                                            : coverable.yearlyPrice?.amount ?? 0
                                    }
                                    paymentFrequencyPostfix={
                                        periodicityUnit[selectedPaymentFrequency]
                                    }
                                />
                            </Table.Cell>
                        </Table.Row>
                    )}
                    {coverableProperties?.map((rowInfo: RowInformation) => {
                        return (
                            <TableRow
                                rowInfo={{
                                    ...rowInfo,
                                    labelType:
                                        rowInfo.labelType !== 'modalLink'
                                            ? 'bold'
                                            : rowInfo.labelType
                                }}
                                key={rowInfo.name}
                                shouldAlignTextLeftOnMobile={shouldRenderShoppingBasketDesign}
                            />
                        );
                    })}
                </Table.Body>
            </Table>
        </div>
    );

    if (shouldRenderShoppingBasketDesign) {
        return <Box className="u-border-none u-shadow-none">{importantInformationTable}</Box>;
    }

    return importantInformationTable;
};

export default ImportantInformation;
